import { Box, Paper, Typography } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import { useParams } from "@tanstack/react-router"
import type { FC } from "react"
import { useMemo, useState } from "react"

import { usePurchaseCostsForProduct } from "../hooks/usePurchaseCostsForProduct"
import { usePurchaseCostsPermissions } from "../hooks/usePurchaseCostsPermissions"

import { PurchaseCostFormDialog } from "./PurchaseCosts/PurchaseCostFormDialog"
import { PurchaseCostHistoryDialog } from "./PurchaseCosts/PurchaseCostHistoryDialog"
import { PurchaseCostsSkeleton } from "./PurchaseCosts/PurchaseCostsSkeleton"
import { PurchaseCostsWarehouseFilter } from "./PurchaseCosts/PurchaseCostsWarehouseFilter"
import { PurchaseCostsWarehouseSection } from "./PurchaseCosts/PurchaseCostsWarehouseSection"
import { usePurchaseCostDialogStore } from "./PurchaseCosts/usePurchaseCostDialogStore"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import type { PurchasingCostListEntry } from "@/graphql/codegen/graphql"

const PRODUCT_EDIT_LAYOUT_PATH = "/app/product/$productId/edit/_layout/costs/_layout/purchase-costs"

export const ProductPurchaseCostsContainer: FC = () => {
  const { productId } = useParams({ from: PRODUCT_EDIT_LAYOUT_PATH })
  const productIdNumber = Number(productId)

  const { canEdit } = usePurchaseCostsPermissions()

  const { warehouses, isFetching, refetch } = usePurchaseCostsForProduct(productIdNumber)

  const handleRefetch = () => refetch({ requestPolicy: "network-only" })

  const [selectedWarehouseIds, setSelectedWarehouseIds] = useState<string[]>([])

  const {
    formDialog,
    isFormDialogOpen,
    historyWarehouseAreaId,
    openFormDialog,
    closeFormDialog,
    openHistoryDialog,
    closeHistoryDialog,
  } = usePurchaseCostDialogStore()

  const filteredWarehouses = useMemo(() => {
    if (!selectedWarehouseIds.length) return warehouses
    return warehouses.filter((warehouse) => selectedWarehouseIds.includes(warehouse.warehouseAreaId))
  }, [warehouses, selectedWarehouseIds])

  const handleAddFuture = (warehouseAreaId: string) => {
    openFormDialog({ warehouseAreaId, editingCost: null, isFuture: true })
  }

  const handleAddNew = () => {
    openFormDialog({ warehouseAreaId: null, editingCost: null, isFuture: false })
  }

  const handleEditPackage = (entry: PurchasingCostListEntry) => {
    const warehouse = warehouses.find((current) => current.warehouseAreaId === entry.warehouseAreaId)
    const isFuture = warehouse?.future.some((future) => future.id === entry.id) ?? false
    openFormDialog({ warehouseAreaId: entry.warehouseAreaId, editingCost: entry, isFuture })
  }

  if (isFetching && !warehouses.length) {
    return <PurchaseCostsSkeleton />
  }

  return (
    <Paper elevation={0} className='flex flex-col gap-y-4 border border-gray-400 p-6'>
      <Box className='flex items-center justify-between'>
        <Typography variant='subtitle1' className='text-gray-900'>
          Warehouse Pricing Information
        </Typography>
      </Box>

      {warehouses.length > 0 && (
        <PurchaseCostsWarehouseFilter
          warehouses={warehouses}
          selectedWarehouseIds={selectedWarehouseIds}
          onChange={setSelectedWarehouseIds}
        />
      )}

      {filteredWarehouses.length ? (
        <Box className='flex flex-col gap-y-4'>
          {filteredWarehouses.map((warehouse) => (
            <PurchaseCostsWarehouseSection
              key={warehouse.warehouseAreaId}
              warehouse={warehouse}
              canEdit={canEdit}
              onEditPackage={handleEditPackage}
              onAddFuture={handleAddFuture}
              onShowHistory={openHistoryDialog}
            />
          ))}
        </Box>
      ) : (
        <InformationMissingAlert
          title='No pricing added yet. Set up cost tiers by warehouse to get started.'
          action={
            canEdit && (
              <Button
                color='inherit'
                size='small'
                appearance='outlined'
                className='text-primary'
                startIcon={<AddOutlined />}
                onClick={handleAddNew}
                data-testid='product.purchase-costs.empty-state-add-button'
              >
                Add Vendor Pricing
              </Button>
            )
          }
        />
      )}

      {isFormDialogOpen && formDialog && (
        <PurchaseCostFormDialog
          productId={productIdNumber}
          defaultWarehouseAreaId={formDialog.warehouseAreaId}
          editingCost={formDialog.editingCost}
          onClose={closeFormDialog}
          onSuccess={handleRefetch}
        />
      )}

      {historyWarehouseAreaId && (
        <PurchaseCostHistoryDialog
          productId={productIdNumber}
          warehouseAreaId={historyWarehouseAreaId}
          onClose={closeHistoryDialog}
        />
      )}
    </Paper>
  )
}
